'use client';

import { ENUM_UTILS, MarketId } from '@drift/common';
import { useMemo } from 'react';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useCurrentAccountOpenPerpPositions from './useCurrentAccountOpenPerpPositions';
import useMarketStateStore, {
	MarketListeningSelection,
} from '../stores/useMarketStateStore';
import useDriftClient from './useDriftClient';
import { MarketStatus, MarketType } from '@drift-labs/sdk';
import { PageRoute } from 'src/constants/constants';
import { useAllTradeableMarketIds } from './utils/useAllTradeableMarketIds';
import useOrderbookDisplayStore, {
	OrderbookSource,
} from '../stores/orderbook/useOrderbookDisplayStore';
import { useCurrentAccountOrders } from './query/accountTradingData/useOrders';

export enum MarketStatePriority {
	SelectedMarket = 'OrderbookDisplay',
	BackgroundDeep = 'DeepPriceData',
	BackgroundShallow = 'ShallowPriceData',
}

export type MarketDlobLiquidityCategorisation = {
	[MarketStatePriority.SelectedMarket]?: MarketId[];
	[MarketStatePriority.BackgroundDeep]?: MarketId[];
	[MarketStatePriority.BackgroundShallow]?: MarketId[];
};

export type MarketTrackingState = {
	categories: MarketDlobLiquidityCategorisation;
	allMarkets: MarketId[];
	totalMarkets: number;
};

const FILTER_DELISTED_MARKETS = true;

const MARKETS_STATUS_TO_SKIP = [MarketStatus.DELISTED, MarketStatus.SETTLEMENT];

const shouldSkipMarketStatus = (status: MarketStatus | undefined) =>
	MARKETS_STATUS_TO_SKIP.some((statusToSkip) =>
		ENUM_UTILS.match(status, statusToSkip)
	);

const isSameOrderbookSource = (
	orderbookSource: OrderbookSource,
	marketDataSource: MarketListeningSelection
) => {
	return (
		(ENUM_UTILS.match(orderbookSource, OrderbookSource.POLLING) &&
			ENUM_UTILS.match(
				marketDataSource,
				MarketListeningSelection.DLOB_SERVER_POLLING
			)) ||
		(ENUM_UTILS.match(orderbookSource, OrderbookSource.BLOCKCHAIN) &&
			ENUM_UTILS.match(marketDataSource, MarketListeningSelection.BLOCKCHAIN))
	);
};

/**
 * To  optimise bandwidth usage, we only want to track the DLOB state for NECESSARY markets, instead of all of them. This hook is responsible for determining the list of markets that need to be tracked at any point in the UI.
 *
 * The Logic is:
 *
 * We have three different "types" of price data being tracked:
 * - Orderbook Display Data : Currently Selected market only => basically depends on user's grouping size selected
 * -- We skip this when we're not on a trade page though.
 * - Deep price data : Current Positions and Currently Selected market => necessary to calculate price impact etc. This is required for these markets because they are the ones where the UI may require this data.
 * - Shallow price data : Other markets => really just necessary to correctly show the current price of markets wherever it may be displayed in the UI.
 */
const useMarketsForDlobToTrack = (
	filterByListeningSelection?: MarketListeningSelection
): MarketTrackingState => {
	const allTradeableMarketIds = useAllTradeableMarketIds();
	const currentlySelectedMarketId = useDriftStore(
		(s) => s.selectedMarket.marketId
	);
	const currentPerpPositions = useCurrentAccountOpenPerpPositions();
	const currentOpenOrders = useCurrentAccountOrders();
	const getSubscriptionStateForMarket = useMarketStateStore(
		(s) => s.getSubscriptionStateForMarket
	);

	const isOnPageWithOrderbook = useOrderbookDisplayStore(
		(s) => s.isDisplayingOrderbook
	);
	const isOnTradePage = useDriftStore(
		(s) => s.currentPageRoute === PageRoute.trade
	);

	const orderbookSource = useOrderbookDisplayStore((s) => s.orderbookSource);
	const shouldBeUsedForOrderbook = !ENUM_UTILS.match(
		orderbookSource,
		OrderbookSource.WEBSOCKET
	);

	const driftClient = useDriftClient();
	const isOnPageRequiringSelectedMarketDeepLiquidity =
		isOnPageWithOrderbook || isOnTradePage;

	const perpMarketsToSkip: Set<string> = useMemo(() => {
		if (!driftClient?.isSubscribed) return new Set();

		return new Set(
			allTradeableMarketIds
				.filter((marketId) => marketId.isPerp)
				.filter((marketId) => {
					const marketStatus = driftClient.getPerpMarketAccount(
						marketId.marketIndex
					)?.status;

					return shouldSkipMarketStatus(marketStatus);
				})
				.map((marketId) => marketId.key)
		);
	}, [driftClient?.isSubscribed]);

	const spotMarketsToSkip: Set<string> = useMemo(() => {
		if (!driftClient?.isSubscribed) return new Set();

		return new Set(
			allTradeableMarketIds
				.filter((marketId) => marketId.isSpot)
				.filter((marketId) => {
					const marketStatus = driftClient.getSpotMarketAccount(
						marketId.marketIndex
					)?.status;

					return shouldSkipMarketStatus(marketStatus);
				})
				.map((marketId) => marketId.key)
		);
	}, [driftClient?.isSubscribed]);

	// Use a more efficient selector that doesn't recompute on every render
	const marketSubscriptionState = useMarketStateStore(
		(s) => s.marketSubscriptionState
	);
	const marketSubscriptionStateUniqueness = useMemo(
		() =>
			Object.entries(marketSubscriptionState)
				.map(
					([key, subscriptionState]) =>
						`${key}_${subscriptionState.listeningSelection}`
				)
				.sort()
				.join(','),
		[marketSubscriptionState]
	);

	// Memoize perp positions into a string so this doesn't cause a re-render if it's just the size of a position that changes
	const perpPositionIndexes = useMemo(() => {
		return currentPerpPositions.map((p) => p.marketIndex).join(',');
	}, [currentPerpPositions]);

	// Memoize open orders market indexes to include them in deep listening selection
	const openOrderMarketIndexes = useMemo(() => {
		if (!currentOpenOrders?.length)
			return [] as { marketIndex: number; marketType: MarketType }[];

		return currentOpenOrders.map((order) => ({
			marketIndex: order.marketIndex,
			marketType: order.marketType,
		}));
	}, [currentOpenOrders]);

	const listeningSelectionFilter = (marketId: MarketId) => {
		if (!filterByListeningSelection) return true;

		const subscriptionState = getSubscriptionStateForMarket(marketId);
		const marketListeningSelection = subscriptionState?.listeningSelection;

		if (!marketListeningSelection) {
			// If no marketListeningSelection is set, default to including it
			// (especially for position markets that should be tracked)
			return true;
		}

		return (
			marketListeningSelection === filterByListeningSelection ||
			isSameOrderbookSource(orderbookSource, filterByListeningSelection)
		);
	};

	const delistedMarketFilter = (marketId: MarketId) => {
		if (!FILTER_DELISTED_MARKETS) return true;

		const inPerpSkip = perpMarketsToSkip.has(marketId.key);
		const inSpotSkip = spotMarketsToSkip.has(marketId.key);
		return !inPerpSkip && !inSpotSkip;
	};

	const returnValue = useMemo(() => {
		// Deep Markets should be all markets with an open position, except the currently selected market
		const allPositionMarkets = perpPositionIndexes
			.split(',')
			// When string is empty, the split results in an array with an empty string, filter this out
			.filter((a) => !!a)
			.map((index) => MarketId.createPerpMarket(+index));

		// Add markets with open orders to deep listening selection
		const openOrderMarkets = openOrderMarketIndexes.map((order) => {
			if (ENUM_UTILS.match(order.marketType, MarketType.PERP)) {
				return MarketId.createPerpMarket(order.marketIndex);
			} else {
				return MarketId.createSpotMarket(order.marketIndex);
			}
		});

		// Combine position markets and open order markets, removing duplicates
		const allDeepMarkets = [...allPositionMarkets, ...openOrderMarkets];
		const uniqueDeepMarkets = allDeepMarkets.filter(
			(market, index, self) =>
				index === self.findIndex((m) => m.key === market.key)
		);

		const deepPriceMarketsInfo = uniqueDeepMarkets.filter((market) => {
			return market.key !== currentlySelectedMarketId.key;
		});

		// Shallow markets should be all other markets (not an open position, not the selected market)
		const shallowPriceMarketsInfo = allTradeableMarketIds.filter((market) => {
			return (
				market.key !== currentlySelectedMarketId.key &&
				!deepPriceMarketsInfo.find(
					(deepMarket) => deepMarket.key === market.key
				)
			);
		});

		// If we're not on the trade page OR orderbook is not being used for display - then we can ignore the extra deep "selected market subscription". But we should still track it in the regular deep category.
		const selectedMarkets =
			isOnPageRequiringSelectedMarketDeepLiquidity && shouldBeUsedForOrderbook
				? [currentlySelectedMarketId]
				: [];

		const deepMarkets = isOnPageRequiringSelectedMarketDeepLiquidity
			? deepPriceMarketsInfo
			: [...deepPriceMarketsInfo, currentlySelectedMarketId];

		const marketCategories: MarketTrackingState['categories'] = {
			[MarketStatePriority.SelectedMarket]: selectedMarkets
				.filter(listeningSelectionFilter)
				.filter(delistedMarketFilter),
			[MarketStatePriority.BackgroundDeep]: deepMarkets
				.filter(listeningSelectionFilter)
				.filter(delistedMarketFilter),
			[MarketStatePriority.BackgroundShallow]: shallowPriceMarketsInfo
				.filter(listeningSelectionFilter)
				.filter(delistedMarketFilter),
		};

		return {
			categories: marketCategories,
			allMarkets: [
				...(marketCategories[MarketStatePriority.SelectedMarket] ?? []),
				...(marketCategories[MarketStatePriority.BackgroundDeep] ?? []),
				...(marketCategories[MarketStatePriority.BackgroundShallow] ?? []),
			],
			totalMarkets:
				(marketCategories[MarketStatePriority.SelectedMarket]?.length ?? 0) +
				(marketCategories[MarketStatePriority.BackgroundDeep]?.length ?? 0) +
				(marketCategories[MarketStatePriority.BackgroundShallow]?.length ?? 0),
		};
	}, [
		perpPositionIndexes,
		openOrderMarketIndexes,
		currentlySelectedMarketId.key,
		marketSubscriptionStateUniqueness,
		isOnTradePage,
		isOnPageWithOrderbook,
		perpMarketsToSkip,
		spotMarketsToSkip,
		allTradeableMarketIds,
		orderbookSource,
		shouldBeUsedForOrderbook,
		filterByListeningSelection,
	]);

	return returnValue;
};

export default useMarketsForDlobToTrack;
